import { z } from "zod";
import { MonthlyPnlMatrixSchema } from "@chro/shared";

const amount = z.number().finite().nullable();
const optionalLabel = z.string().nullable();
const contract = z.object({ code: z.string(), expYear: z.number(), expMonth: z.number() });
const recognitionLine = z.object({
  exitKind: z.enum(["close", "delivery"]),
  recognitionDate: z.string(),
  exitDate: optionalLabel.optional(),
  book: optionalLabel,
  trader: optionalLabel,
  pair: optionalLabel,
  contract,
  entry: z.number().finite(),
  exit: z.number().finite(),
  volume: z.number().finite(),
  unit: z.string(),
  priceUnit: z.string(),
  nativeCcy: z.enum(["EUR", "GBP"]),
  nativeAmount: z.number().finite(),
  recognizedEUR: amount,
  finality: z.enum(["final", "estimate", "stale"]),
  entryTradeId: optionalLabel,
  exitTradeId: optionalLabel,
  reclass: z.object({ id: z.string(), name: optionalLabel, fromMonth: z.string() }).nullable().optional(),
  schedule: z.object({
    id: z.string(), itemId: z.string(), sourceMonth: z.string(), sourceDate: z.string(),
    sourceQuantity: z.number().finite(), allocatedQuantity: z.number().finite(), percent: z.number().finite(),
    reason: z.string(), remainder: z.boolean(),
  }).nullable().optional(),
});
const position = z.object({
  lots: z.number().finite(),
  volume: z.number().finite(),
  avgAcquisitionPrice: amount,
  acquisitionCost: amount,
  markPrice: amount,
  markDate: optionalLabel,
  carryAmount: amount,
});

export const FinanceReportSettingsSchema = z.object({
  baselineMonth: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).refine((value) => value >= "2000-01").nullable(),
});

export const FinanceReportBaselineSchema = z.object({
  startDate: z.string(),
  openingDate: z.string(),
  endDate: z.string(),
  initialValue: z.literal(0),
  totalsByCurrency: z.array(z.object({
    currency: z.string(),
    recognizedPnl: amount,
    openingCarry: amount,
    closingCarry: amount,
    openingAdjustment: amount,
    reclassTimingAdjustment: amount,
    roundingAdjustment: amount,
    portfolioResult: amount,
  })),
  basis: z.string(),
});

/** Validate report amounts before presenting them as finance figures. */
export const MonthlyFinanceReportSchema = z.object({
  schemaVersion: z.literal(1),
  companyId: z.string(),
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  generatedAt: z.string(),
  // Older servers may omit this presentation-only cumulative baseline.
  baseline: FinanceReportBaselineSchema.nullable().optional(),
  monthlyPnlMatrix: MonthlyPnlMatrixSchema.optional(),
  source: z.object({
    asOf: z.string(),
    inputFingerprint: z.string(),
    recognition: z.literal("current_policy"),
    carry: z.literal("current_policy_reconstruction"),
    periodStatus: z.enum(["completed_month", "draft_month"]),
  }),
  recognition: z.object({
    liveTotalEur: amount,
    approvedTotalEur: amount,
    approvedSubtotalEur: z.number().finite(),
    approvalComplete: z.boolean(),
    closeEur: amount,
    deliveryEur: amount,
    roundingAdjustmentEur: amount,
    cells: z.array(z.object({
      book: optionalLabel,
      trader: optionalLabel,
      state: z.enum(["open", "soft", "hard"]),
      liveEur: amount,
      approvedEur: amount,
      snapshotId: optionalLabel,
      approvedAt: optionalLabel,
      differenceEur: amount,
    })),
    liveLines: z.array(recognitionLine),
    approvedLines: z.array(recognitionLine.extend({ id: z.string(), snapshotId: z.string() })),
  }),
  carry: z.object({
    month: z.string(),
    openingDate: z.string(),
    closingDate: z.string(),
    rows: z.array(z.object({
      key: z.string(),
      book: optionalLabel,
      trader: optionalLabel,
      pair: optionalLabel,
      broker: optionalLabel,
      venue: optionalLabel,
      contract,
      nativeCcy: z.string(),
      unit: z.string(),
      priceUnit: z.string(),
      side: z.enum(["B", "S"]),
      opening: position,
      closing: position,
      tradeBuyLots: z.number().finite(),
      tradeSellLots: z.number().finite(),
    })),
  }),
  carryTotalsByCurrency: z.array(z.object({ currency: z.string(), opening: amount, closing: amount })),
  vm: z.object({
    statements: z.array(z.object({
      id: z.string(),
      reportDate: z.string(),
      paymentDate: optionalLabel,
      currency: z.string(),
      variationMargin: amount,
      fees: amount,
    })),
    totalsByCurrency: z.array(z.object({ currency: z.string(), amount, statementCount: z.number().int() })),
    eurTotal: amount,
    computedEur: amount,
    differenceEur: amount,
    notes: z.array(z.string()),
    days: z.array(z.object({
      date: z.string(),
      paymentDate: optionalLabel,
      bankTotal: amount,
      computedTotal: z.number().finite(),
      computedIncomplete: z.boolean(),
      diff: amount,
    })),
    coverage: z.enum(["no_statements", "statement_dates_only"]),
  }),
  scopeNotes: z.array(z.string()),
  bridge: z.object({
    currency: z.literal("EUR"),
    openingCarry: amount,
    computedVm: amount,
    recognizedPnl: amount,
    reclassTimingAdjustment: amount,
    closingCarry: amount,
    difference: amount,
    monthCoverageComplete: z.boolean(),
    absentStatementDates: z.array(z.string()),
    basis: z.string(),
  }),
  checks: z.array(z.object({
    name: z.string(),
    status: z.enum(["pass", "review"]),
    difference: amount,
    detail: z.string(),
  })),
});

export type MonthlyFinanceReportData = z.infer<typeof MonthlyFinanceReportSchema>;

/** A refreshed company setting must not accompany cached figures from another baseline. */
export function financeReportMatchesBaseline(
  report: Pick<MonthlyFinanceReportData, "month" | "baseline">,
  baselineMonth: string | null,
): boolean {
  if (baselineMonth && report.month >= baselineMonth) return report.baseline?.startDate === `${baselineMonth}-01`;
  // Older reports may omit the optional field when no baseline applies.
  return report.baseline == null;
}

export function financeReportPath(month: string, format: "json" | "xlsx" = "json"): string {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month) || month < "2000-01") throw new Error("Select a valid financial month.");
  return `/eex/recognition/finance-report${format === "xlsx" ? ".xlsx" : ""}?month=${encodeURIComponent(month)}`;
}

export function defaultFinanceMonth(now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Berlin", year: "numeric", month: "2-digit",
  }).formatToParts(now);
  const year = Number(parts.find((part) => part.type === "year")!.value);
  const month = Number(parts.find((part) => part.type === "month")!.value);
  const previous = new Date(Date.UTC(year, month - 2, 1));
  return `${previous.getUTCFullYear()}-${String(previous.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** A blank company setting clears the baseline; a value must be a completed month. */
export function financeBaselineMonth(value: string, now = new Date()): string | null {
  const month = value.trim();
  if (!month) return null;
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month) || month < "2000-01" || month > defaultFinanceMonth(now)) {
    throw new Error("Choose a completed reporting baseline month from January 2000 onward.");
  }
  return month;
}
